<template>
  <td
    class="px-5 font-normal"
    :class="[
      column.columnClass,
      {
        'bg-light-grey': props.isChildNode,
        'whitespace-nowrap': noWrap,
        'pr-2': props.isLastColumn,
      },
    ]"
    :data-cy="`table-row-column-${column.key}`"
  >
    <div v-if="hasAccessToSee">
      <lazy-load-component
        v-if="column.component && showComponent"
        :options="lazyLoadOptions"
      >
        <component
          :is="column.component"
          v-bind="rowProps"
          :model-value="row[column.key]"
          class="table-component"
          v-on="rowEventHandlers"
        >
          {{ rowValue }}
        </component>
      </lazy-load-component>

      <div
        v-else-if="column.options"
        class="table-text"
        :class="column.class || ''"
      >
        {{ valueFromOptions }}
      </div>
      <a
        v-else-if="row.externalLink && column.key === 'linkText'"
        target="_blank"
        class="table-text text-blue-600 underline"
        :href="row.externalLink"
      >
        {{ row[column.key] }}
      </a>
      <div
        v-else
        class="table-text"
        v-bind="
          isChildNode
            ? {}
            : {
                style: columnStyle,
                class: column.class || '',
              }
        "
      >
        <router-link
          v-if="column.redirectTo"
          :to="rowRoute"
          class="font-medium"
        >
          {{ formattedValue }}
        </router-link>

        <span v-else-if="isChildNode">
          {{ formattedValue }}
        </span>

        <span v-else>
          <span v-if="accordionTable">
            <div
              v-if="row['hasChild'] && column.key === 'category'"
              class="flex w-40 items-center justify-between"
              @click="onClickRow"
            >
              <p class="text-sm font-bold text-headline">
                {{ formattedValue }}
              </p>
              <icon-base
                height="10"
                width="10"
                :icon="IconArrowUp"
                opacity="0.5"
                class="transform text-gray-500"
                :class="{ 'rotate-180': isChildRowActive && row['open'] }"
              />
            </div>
            <div
              v-else
              class="text-sm"
              :class="
                !row['hasChild'] &&
                column.key === 'category' &&
                !row['fatherName']
                  ? 'font-bold text-headline'
                  : 'pl-5'
              "
            >
              {{ formattedValue }}
            </div>
          </span>
          <div v-else :style="columnStyle">
            {{ formattedValue }}
          </div>
        </span>
      </div>
    </div>
  </td>
  <template v-if="!isChildNode && hasAccessToSee && isSubColumnVisible">
    <table-row-node
      v-for="subColumn in column.subColumns"
      :key="`sub-column-${subColumn.key}`"
      :row="row"
      :row-index="rowIndex"
      :column="subColumn"
      :lazy-load="lazyLoad"
      :td-classes="tdClasses"
      :accordion-table="accordionTable"
      :no-wrap="noWrap"
      is-child-node
    />
  </template>
</template>

<script setup lang="ts">
import { computed, inject, shallowRef } from "vue";
import { useStore } from "vuex";

import get from "lodash/get";

import { isNullish } from "@/helpers/common";
import { SubColumnsVisibilityKey } from "@/helpers/symbols";
import { useAccess } from "@/hooks/access";

import LazyLoadComponent from "@/components/LazyLoad.vue";
import IconArrowUp from "@/components/icons/IconArrowUp.vue";

import type { IDealsColumn } from "@/models/applications";
import type { LazyLoadOptions } from "@/models/common";

const props = defineProps<{
  // Switching to any for flexibility, as typing HAS to be a part of a different work
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: Record<string, any>;
  column: IDealsColumn;
  rowIndex: number;
  lazyLoad?: boolean | LazyLoadOptions;
  tdClasses?: string;
  accordionTable?: boolean;
  isChildNode?: boolean;
  noWrap?: boolean;
  isLastColumn?: boolean;
}>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getByKey = (row: Record<string, any>, key: string) => {
  if (!key || !row) {
    return null;
  }
  if (key.includes(".")) {
    return get(row, key, null);
  }
  return row[key];
};

const store = useStore();
const { hasAccess } = useAccess();

const subColumnsVisibility = inject(SubColumnsVisibilityKey, null);
const isSubColumnVisible = computed(() => {
  if (!props.column.subColumns?.length) {
    return false;
  }
  if (!subColumnsVisibility) {
    return true;
  }
  return !!subColumnsVisibility.value[props.column.key];
});

const isChildRowActive = shallowRef(false);

const rowProps = computed(() => {
  const { row, rowIndex, column } = props;

  const propsFromColumn =
    typeof column.props === "function" ? column.props(row) : column.props || {};

  return {
    model: row,
    value: rowValue.value,
    rowIndex,
    ...propsFromColumn,
  };
});

const hasAccessToSee = computed(() => {
  if (!props.column.acl) {
    return true;
  }
  return hasAccess(new Set(props.column.acl));
});

const columnStyle = computed(() => {
  const { column } = props;

  if (!column.style) {
    return undefined;
  }

  const styles = column.style;
  const value = formattedValue.value;

  if (!styles?.color || !value) {
    return "";
  }
  if (typeof styles.color === "function") {
    return `color: ${styles.color(value)}`;
  }
  return `color: ${
    styles.color[value.toLowerCase() as keyof typeof styles.color] || ""
  }`;
});

const rawRowValue = computed(() => getByKey(props.row, props.column.key));

const rowValue = computed(() =>
  props.column.formatter ? formattedValue.value : rawRowValue.value
);

const lazyLoadOptions = computed(() =>
  typeof props.lazyLoad === "object" ? props.lazyLoad : undefined
);

const valueFromOptions = computed(() => {
  const { row, column } = props;
  const { options, key, placeholder } = column;

  const valueId = get(row, key);
  if (!valueId || !options) {
    return placeholder || "-";
  }
  const allOptions = store.getters["options/all"];
  return get(allOptions, options)?.[valueId];
});

const rowEventHandlers = computed(() => {
  if (!props.column.eventHandlers) {
    return {};
  }
  return Object.entries(props.column.eventHandlers).reduce(
    (result, [handlerName, handlerFunction]) => {
      if (!result) {
        return {};
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result[handlerName] = (...args: any[]) => {
        // Call the handler function with the original arguments and add row at the end
        return handlerFunction(...args, props.row);
      };
      return result;
    },
    {} as IDealsColumn["eventHandlers"]
  );
});

const formattedValue = computed(() => {
  const { column, row } = props;
  if (column.formatModel && column.formatter) {
    return column.formatter(row);
  }
  const value: string = rawRowValue.value;
  if (value === undefined || value === null || value === "") {
    return column.placeholder || "-";
  }
  if (column.formatter) {
    return column.formatter(value) || column.placeholder;
  }
  return value;
});

const showComponent = computed(() => {
  const { column } = props;
  if (!column.removeComponentIfEmpty) {
    return true;
  }
  const value = rawRowValue.value;
  if (
    isNullish(value) ||
    ((Array.isArray(value) || typeof value === "string") && !value.length)
  ) {
    return false;
  }
  return true;
});

const rowRoute = computed(() => {
  const { column, row } = props;
  const redirectTo = column.redirectTo;

  if (!redirectTo) {
    return "";
  }

  const base = `${redirectTo?.base}/`;
  const itemId = redirectTo?.addId ? `${row.id + "/"}` : "";
  const suffix = redirectTo?.suffix ? `${redirectTo.suffix}/` : "";

  return {
    path: base + itemId + suffix,
    query: redirectTo?.query || {},
  };
});

const onClickRow = () => {
  isChildRowActive.value = !isChildRowActive.value;
};

const nodeWidth = computed(() => {
  const allowedWiderColumnKeys = ["placementData.risk_tier"];

  return allowedWiderColumnKeys.includes(props.column.key)
    ? "fit-content"
    : "320px";
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.table-component,
.table-text {
  /* design system limits width to 320px with ellipsis truncation */
  max-width: v-bind(nodeWidth);
}

.table-component:has(.toggle-section__header) {
  @apply w-full max-w-none;
}
</style>
